import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UtensilsCrossed, ShoppingCart, RefreshCw, ChevronRight,
  Calendar, ChefHat, ChevronDown, ChevronUp,
  Beef, Zap, Droplet, Coffee, Sun, Moon, Cookie, Flame,
  Download, Copy, Check, Lock, CheckCircle2, Sparkles, X, Plus,
  Bookmark, BookmarkCheck, RotateCcw, TrendingUp, Clock, LayoutDashboard,
  BookOpen, Search, Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTheme } from '../contexts/ThemeContext';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { SEOMeta } from '@/components/SEOMeta';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';
import { CheckinModal } from '@/components/CheckinModal';
import { ProgressSection } from '@/components/ProgressSection';
import { CinematicLoader } from '@/components/CinematicLoader';
import { DashboardTour } from '@/components/DashboardTour';

const PLAN_BREADCRUMBS = [{ label: 'Моят план', href: '/my-plan' }];

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MEAL_CONFIG = {
  breakfast: { icon: Coffee,  label: 'Закуска', accent: 'text-amber-600',   bg: 'bg-amber-100',   cardBg: 'bg-white dark:bg-[#2a2319]',  border: 'border-gray-100 dark:border-amber-700/35'   },
  lunch:     { icon: Sun,     label: 'Обяд',    accent: 'text-sky-600',     bg: 'bg-sky-100',     cardBg: 'bg-white dark:bg-[#162029]',  border: 'border-gray-100 dark:border-sky-700/35'     },
  dinner:    { icon: Moon,    label: 'Вечеря',  accent: 'text-violet-600',  bg: 'bg-violet-100',  cardBg: 'bg-white dark:bg-[#1d1a2a]',  border: 'border-gray-100 dark:border-violet-700/35'  },
  snack:     { icon: Cookie,  label: 'Снак',    accent: 'text-emerald-600', bg: 'bg-emerald-100', cardBg: 'bg-white dark:bg-[#19271d]',  border: 'border-gray-100 dark:border-emerald-700/35' },
};

const BENEFITS = [
  'Нов 7-дневен персонализиран план всяка седмица',
  'Край на чуденето "Какво да готвя днес?" (включени лесни рецепти)',
  'Точно изчислени макроси и калории за твоите цели',
  'Автоматичен списък за пазаруване за магазина',
  'Неограничена смяна на рецепти по всяко време',
];

/* ── Macro chip ── */
const MacroChip = ({ icon: Icon, label, value, iconClass, chipBg, darkChipBg }) => (
  <div className={`flex-1 ${chipBg} ${darkChipBg || ''} rounded-xl p-3 flex flex-col items-center gap-1.5`}>
    <div className="w-8 h-8 rounded-lg bg-white dark:bg-slate-700 shadow-sm flex items-center justify-center">
      <Icon className={`w-4 h-4 ${iconClass}`} />
    </div>
    <p className="text-sm font-bold text-gray-900 dark:text-gray-100 tabular-nums leading-none">
      {value}<span className="text-xs font-normal text-gray-400 dark:text-gray-500 dark:text-gray-400 dark:text-gray-500">г</span>
    </p>
    <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-none">{label}</p>
  </div>
);

/* ── Macro Ring ── */
const MacroRing = ({ value, target, color, trackColor, label, delay = 0 }) => {
  const r = 34;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(value / Math.max(target, 1), 1);
  const offset = circ * (1 - pct);
  return (
    <div className="flex flex-col items-center gap-1.5">
      {/* Ring + centered HTML text overlay */}
      <div className="relative" style={{ width: 88, height: 88 }}>
        <svg width="88" height="88" viewBox="0 0 88 88" style={{ display: 'block' }}>
          {/* Track */}
          <circle cx="44" cy="44" r={r} fill="none" stroke={trackColor} strokeWidth="7" />
          {/* Progress */}
          <motion.circle
            cx="44" cy="44" r={r}
            fill="none"
            stroke={color}
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={`${circ} ${circ}`}
            initial={{ strokeDashoffset: circ }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 1.1, delay, ease: [0.34, 1.2, 0.64, 1] }}
            style={{ transformOrigin: '44px 44px', transform: 'rotate(-90deg)' }}
          />
        </svg>
        {/* Centered text — HTML overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
          <span className="text-base font-extrabold leading-none text-gray-900 dark:text-white tabular-nums">
            {value}<span className="text-[10px] font-normal opacity-60">г</span>
          </span>
          <span className="text-sm font-bold leading-none tabular-nums" style={{ color }}>
            {Math.round(pct * 100)}%
          </span>
        </div>
      </div>
      <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 text-center">{label}</p>
    </div>
  );
};

const MacroRings = ({ day, plan }) => {
  const totals = (day?.meals || []).reduce(
    (a, m) => ({ p: a.p + (m.proteins || 0), c: a.c + (m.carbs || 0), f: a.f + (m.fats || 0) }),
    { p: 0, c: 0, f: 0 }
  );
  const cal = plan?.target_calories || 2000;
  const targets = {
    p: Math.round(cal * 0.30 / 4),
    c: Math.round(cal * 0.45 / 4),
    f: Math.round(cal * 0.25 / 9),
  };
  return (
    <div className="flex items-center justify-center gap-6 sm:gap-10 pt-2 pb-1">
      <MacroRing value={Math.round(totals.p)} target={targets.p} color="#34d399" trackColor="rgba(52,211,153,0.12)" label="Белтъчини"     delay={0.2} />
      <MacroRing value={Math.round(totals.c)} target={targets.c} color="#fbbf24" trackColor="rgba(251,191,36,0.12)"  label="Въглехидрати" delay={0.35} />
      <MacroRing value={Math.round(totals.f)} target={targets.f} color="#38bdf8" trackColor="rgba(56,189,248,0.12)"  label="Мазнини"      delay={0.5} />
    </div>
  );
};

/* ── Meal Card — 3D flip ── */
const MealCard = ({ meal, planId, savedMeals, onToggleSave }) => {
  const [flipped, setFlipped] = useState(false);
  const [saving, setSaving] = useState(false);
  const { theme } = useTheme();
  const cfg = MEAL_CONFIG[meal.type] || MEAL_CONFIG.lunch;
  const MealIcon = cfg.icon;
  const isSaved = savedMeals?.has(meal.name);
  const hasBack = meal.ingredients?.length > 0 || meal.recipe?.length > 0;

  const handleBookmark = async (e) => {
    e.stopPropagation();
    if (!onToggleSave) return;
    setSaving(true);
    await onToggleSave(meal, planId, isSaved);
    setSaving(false);
  };

  return (
    <div
      className={`relative select-none ${hasBack ? 'cursor-pointer' : ''}`}
      style={{ perspective: '1100px' }}
      onClick={() => hasBack && setFlipped(f => !f)}
      data-testid={`meal-card-${meal.name?.slice(0,10).replace(/\s/g,'-')}`}
    >
      <div
        className="grid w-full transition-transform duration-500"
        style={{
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          transitionTimingFunction: 'cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* ────── FRONT FACE ────── */}
        <div
          className={`[grid-area:1/1] rounded-2xl border overflow-hidden ${cfg.border}`}
          style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
            boxShadow: '0 1px 0 rgba(0,0,0,0.04), 0 8px 20px rgba(0,0,0,0.05)' }}
        >
          <div className={`flex items-center justify-between px-4 py-3 border-b ${cfg.border} ${cfg.cardBg}`}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-lg ${cfg.bg} flex items-center justify-center`}>
                <MealIcon className={`w-3.5 h-3.5 ${cfg.accent}`} />
              </div>
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">{meal.type_label}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Flame className="w-3.5 h-3.5 text-orange-400" />
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100 tabular-nums">{meal.calories}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500 ml-0.5">ккал</span>
              </div>
              {onToggleSave && (
                <button onClick={handleBookmark} disabled={saving}
                  data-testid={`meal-bookmark-${meal.name?.slice(0,10).replace(/\s/g,'-')}`}
                  title={isSaved ? 'Премахни от книгата' : 'Запази в книгата'}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    isSaved ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                    : 'bg-gray-50 dark:bg-slate-700 text-gray-400 dark:text-gray-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:text-emerald-600 dark:hover:text-emerald-400'
                  } ${saving ? 'opacity-50' : ''}`}
                >
                  {isSaved ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>
          </div>
          <div className={`p-5 ${cfg.cardBg}`}>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-base leading-snug mb-4">{meal.name}</h4>
            <div className="flex gap-2">
              <MacroChip icon={Beef}    label="Белтъци"      value={meal.proteins} iconClass="text-emerald-600 dark:text-emerald-400" chipBg="bg-emerald-50" darkChipBg="dark:bg-emerald-900/20" />
              <MacroChip icon={Zap}     label="Въглехидрати" value={meal.carbs}    iconClass="text-amber-500"   chipBg="bg-amber-50"   darkChipBg="dark:bg-amber-900/20" />
              <MacroChip icon={Droplet} label="Мазнини"      value={meal.fats}     iconClass="text-sky-500"     chipBg="bg-sky-50"     darkChipBg="dark:bg-sky-900/20" />
            </div>
            {hasBack && (
              <div className="mt-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold
                  bg-emerald-50 dark:bg-emerald-900/25
                  text-emerald-700 dark:text-emerald-400
                  border border-emerald-200/80 dark:border-emerald-700/40">
                  <RotateCcw className="w-3 h-3" />
                  Виж рецептата
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ────── BACK FACE (glassmorphism) ────── */}
        <div
          className="[grid-area:1/1] rounded-2xl overflow-hidden border dark:border-emerald-900/25 border-gray-200/60"
          style={{
            backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
            background: 'var(--meal-back-bg)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.06)',
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b dark:border-white/[0.06] border-gray-100 bg-white/10 dark:bg-white/[0.03]">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center">
                <ChefHat className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-widest">Рецепта</span>
            </div>
            <span className="text-[10px] text-gray-600 dark:text-gray-400 max-w-[130px] truncate">{meal.name}</span>
          </div>

          {meal.ingredients?.length > 0 && (
            <div className="px-4 pt-4 pb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-700 dark:text-gray-400 mb-2.5">Съставки</p>
              <ul className="space-y-1.5">
                {meal.ingredients.map((ing, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-800 dark:text-gray-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                    {ing}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {meal.recipe?.length > 0 && (
            <div className="px-4 pb-4 pt-3 border-t dark:border-white/[0.05] border-gray-200 mt-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-700 dark:text-gray-400 mb-3">Приготвяне</p>
              <ol className="space-y-2.5">
                {meal.recipe.map((step, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-emerald-600 dark:bg-emerald-500/80 text-white font-bold flex items-center justify-center text-[9px] mt-0.5">{i + 1}</span>
                    <span className="text-xs text-gray-800 dark:text-gray-200 leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="flex items-center gap-1.5 px-4 pb-4 text-[10px] text-gray-500 dark:text-gray-600">
            <RotateCcw className="w-2.5 h-2.5" />
            <span>Натисни за обратно</span>
          </div>
        </div>
      </div>
    </div>
  );
};


/* ── Tag chip colors ── */
const TAG_COLORS = {
  'Поддържане': 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400 border-sky-100 dark:border-sky-700/30',
  'Отслабване': 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border-rose-100 dark:border-rose-700/30',
  'Покачване на маса': 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400 border-violet-100 dark:border-violet-700/30',
  'Веган': 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-700/30',
  'Традиционен': 'bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-gray-100 dark:border-slate-600',
};
const PlanTag = ({ label }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${TAG_COLORS[label] || 'bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-gray-100 dark:border-slate-600'}`}>{label}</span>
);

/* ── PDF generation ── */
const escH = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const MEAL_PDF_STYLE = {
  breakfast: { label: 'ЗАКУСКА',  color: '#b45309', bg: '#fffbeb', border: '#fcd34d', dot: '#f59e0b' },
  lunch:     { label: 'ОБЯД',     color: '#0369a1', bg: '#f0f9ff', border: '#7dd3fc', dot: '#0284c7' },
  dinner:    { label: 'ВЕЧЕРЯ',   color: '#6d28d9', bg: '#f5f3ff', border: '#c4b5fd', dot: '#7c3aed' },
  snack:     { label: 'СНАК',     color: '#065f46', bg: '#ecfdf5', border: '#6ee7b7', dot: '#059669' },
};

const buildPrintHTML = (plan) => {
  const tdeeData = JSON.parse(localStorage.getItem('portion_tdee') || 'null');
  const excludedFoods = tdeeData?.excludedFoods || [];
  const today = new Date().toLocaleDateString('bg-BG', { day: 'numeric', month: 'long', year: 'numeric' });

  const GOAL_TAG_STYLE = {
    'Отслабване':         'background:#fff1f2;border:1px solid #fecdd3;color:#be123c',
    'Покачване на маса':  'background:#f5f3ff;border:1px solid #ddd6fe;color:#5b21b6',
    'Поддържане':         'background:#f0f9ff;border:1px solid #bae6fd;color:#0369a1',
  };
  const goalStyle = GOAL_TAG_STYLE[plan.goal] || 'background:#f0fdf4;border:1px solid #d1fae5;color:#065f46';

  const daysHTML = (plan.days || []).map(day => {
    const mealsHTML = (day.meals || []).map(meal => {
      const mt = (meal.type || 'lunch').toLowerCase();
      const ps = MEAL_PDF_STYLE[mt] || MEAL_PDF_STYLE.lunch;
      const ings = (meal.ingredients || []).length > 0
        ? `<div class="meal-ings">${meal.ingredients.slice(0,5).map(escH).join(' &middot; ')}</div>`
        : '';
      return `
        <div class="meal" style="border:1.5px solid ${ps.border};border-radius:10px;overflow:hidden">
          <div class="meal-hdr" style="background:${ps.bg};padding:6px 10px;display:flex;align-items:center;gap:6px;border-bottom:1px solid ${ps.border}">
            <span class="meal-dot" style="width:8px;height:8px;border-radius:50%;background:${ps.dot};flex-shrink:0;display:inline-block"></span>
            <span class="meal-type" style="font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:.1em;color:${ps.color}">${ps.label}</span>
            <span style="margin-left:auto;font-size:10px;font-weight:700;color:#374151">${meal.calories} <span style="font-weight:400;color:#9ca3af">ккал</span></span>
          </div>
          <div class="meal-body" style="padding:8px 10px">
            <div class="meal-name" style="font-size:11.5px;font-weight:700;color:#111827;margin-bottom:6px;line-height:1.35">${escH(meal.name)}</div>
            <div class="macros">
              <span class="macro-p">Б <strong>${meal.proteins}г</strong></span>
              <span class="macro-c">В <strong>${meal.carbs}г</strong></span>
              <span class="macro-f">М <strong>${meal.fats}г</strong></span>
            </div>
            ${ings}
          </div>
        </div>`;
    }).join('');

    return `
      <div class="day">
        <div class="day-hdr">
          <div class="day-bar"></div>
          <span class="day-name">${escH(day.day_name)}</span>
          <span class="day-cal">${day.total_calories} ккал</span>
        </div>
        <div class="meals-grid">${mealsHTML}</div>
      </div>`;
  }).join('');

  const shop = plan.shopping_list?.standard;
  const shopHTML = shop ? `
    <div class="pb">
      <div class="sec-hdr">
        <div class="sec-icon">&#128722;</div>
        <div>
          <div class="sec-title">Списък за пазар</div>
          <div class="sec-sub">Стандартен бюджет &mdash; ${escH(shop.weekly_estimate)}/седм.</div>
        </div>
      </div>
      <div class="shop-grid">
        ${(shop.categories || []).map(cat => `
          <div class="cat">
            <div class="cat-name">${escH(cat.name)}</div>
            ${(cat.items || []).map(item => `
              <div class="shop-row">
                <span class="shop-item">${escH(item.name)}</span>
                <span class="shop-amt">${escH(item.amount)}</span>
              </div>`).join('')}
          </div>`).join('')}
      </div>
    </div>` : '';

  const css = `
    *{box-sizing:border-box;margin:0;padding:0}
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
    body{font-family:'Inter','Segoe UI',Arial,sans-serif;font-size:12px;color:#111827;background:#fff;line-height:1.5;padding:20px 24px}

    /* ── Branded Header ── */
    .cover{background:linear-gradient(135deg,#052e16 0%,#064e3b 60%,#065f46 100%);color:#fff;padding:24px 28px 20px;margin:-20px -24px 0}
    .cover-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px}
    .logo-mark{display:flex;align-items:center;gap:10px}
    .logo-icon{width:36px;height:36px;background:#10b981;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:900;color:#fff;letter-spacing:-1px;flex-shrink:0}
    .logo-text{font-size:20px;font-weight:800;color:#fff;letter-spacing:-.3px}
    .logo-sub{font-size:10px;color:#6ee7b7;font-weight:500;letter-spacing:.05em;margin-top:1px}
    .cover-date{font-size:11px;color:#a7f3d0;text-align:right}
    .cover-tags{display:flex;gap:8px;flex-wrap:wrap}
    .ctag{padding:4px 12px;border-radius:20px;font-size:10px;font-weight:700;letter-spacing:.03em}
    .ctag-goal{background:rgba(255,255,255,0.15);color:#fff;border:1px solid rgba(255,255,255,0.25)}
    .ctag-cal{background:#059669;color:#fff}
    .ctag-diet{background:rgba(255,255,255,0.1);color:#d1fae5;border:1px solid rgba(255,255,255,0.15)}
    .ctag-excl{background:rgba(190,18,60,0.7);color:#fff}
    .cover-title{font-size:13px;color:#d1fae5;font-weight:500;margin-top:10px}

    /* ── Section Headers ── */
    .sec-hdr{display:flex;align-items:center;gap:12px;margin:24px 0 14px;padding:10px 14px;background:#f9fafb;border-radius:8px;border-left:4px solid #059669}
    .sec-icon{font-size:18px}
    .sec-title{font-size:13px;font-weight:800;color:#111827}
    .sec-sub{font-size:10px;color:#6b7280;margin-top:1px}

    /* ── Plan section header ── */
    .plan-sec-hdr{display:flex;align-items:center;gap:12px;margin:20px 0 16px;padding:10px 14px;background:#f0fdf4;border-radius:8px;border-left:4px solid #059669}

    /* ── Day ── */
    .day{margin-bottom:20px;page-break-inside:avoid}
    .day-hdr{display:flex;align-items:center;gap:10px;margin-bottom:10px;padding:8px 14px;background:#111827;border-radius:8px}
    .day-bar{width:4px;height:16px;background:#059669;border-radius:2px;flex-shrink:0}
    .day-name{font-weight:800;font-size:13px;color:#fff;flex:1}
    .day-cal{font-size:11px;font-weight:700;color:#6ee7b7;background:rgba(5,150,105,0.25);padding:2px 10px;border-radius:12px}

    /* ── Meals Grid ── */
    .meals-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
    .meal{border-radius:10px;overflow:hidden}
    .meal-name{font-size:11.5px;font-weight:700;color:#111827;margin-bottom:6px;line-height:1.35}
    .macros{display:flex;gap:5px;flex-wrap:wrap;margin-bottom:5px}
    .macro-p{background:#ecfdf5;color:#065f46;padding:2px 7px;border-radius:5px;font-size:10px}
    .macro-c{background:#fffbeb;color:#92400e;padding:2px 7px;border-radius:5px;font-size:10px}
    .macro-f{background:#f0f9ff;color:#0c4a6e;padding:2px 7px;border-radius:5px;font-size:10px}
    .meal-ings{font-size:9.5px;color:#6b7280;border-top:1px solid #f3f4f6;padding-top:5px;margin-top:2px;line-height:1.4}

    /* ── Shopping ── */
    .pb{page-break-before:always;padding-top:8px}
    .shop-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px 28px}
    .cat{margin-bottom:14px}
    .cat-name{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:#059669;padding-bottom:5px;border-bottom:2px solid #d1fae5;margin-bottom:7px}
    .shop-row{display:flex;justify-content:space-between;align-items:center;padding:3.5px 0;border-bottom:1px solid #f9fafb}
    .shop-item{font-size:11px;color:#374151}
    .shop-amt{font-size:10.5px;color:#6b7280;font-weight:600}

    /* ── Footer ── */
    .footer{margin-top:28px;padding:12px 0;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center}
    .footer-brand{font-size:10.5px;font-weight:700;color:#059669}
    .footer-note{font-size:9.5px;color:#9ca3af}

    @page{margin:0}
    @media print{.pb{page-break-before:always}body{padding:20px 24px}.cover{margin:-20px -24px 0;padding:20px 24px 18px}}
  `;

  const dietLabel = tdeeData?.diet === 'vegan' ? 'Веган' : 'Традиционен';

  return `<!DOCTYPE html>
<html lang="bg">
<head>
  <meta charset="UTF-8">
  <title>portion.bg — Хранителен план</title>
  <style>${css}</style>
</head>
<body>

  <div class="cover">
    <div class="cover-top">
      <div class="logo-mark">
        <div class="logo-icon">P</div>
        <div>
          <div class="logo-text">PORTION</div>
          <div class="logo-sub">BUDGET MEALS · PORTION.BG</div>
        </div>
      </div>
      <div class="cover-date">Генериран на<br><strong>${escH(today)}</strong></div>
    </div>
    <div class="cover-tags">
      <span class="ctag ctag-goal">${escH(plan.goal || '—')}</span>
      <span class="ctag ctag-cal">${plan.target_calories} ккал/ден</span>
      <span class="ctag ctag-diet">${escH(dietLabel)}</span>
      ${excludedFoods.length > 0 ? `<span class="ctag ctag-excl">Изключени: ${excludedFoods.length} продукта</span>` : ''}
    </div>
    <div class="cover-title">Персонализиран 7-дневен хранителен план</div>
  </div>

  <div class="plan-sec-hdr">
    <div class="sec-icon">&#127859;</div>
    <div>
      <div class="sec-title">7-дневен хранителен план</div>
      <div class="sec-sub">Закуска &middot; Обяд &middot; Вечеря &middot; Снак за всеки ден</div>
    </div>
  </div>

  ${daysHTML}
  ${shopHTML}

  <div class="footer">
    <span class="footer-brand">portion.bg</span>
    <span class="footer-note">Информацията е с образователна цел. Консултирай се с нутриционист.</span>
  </div>

</body>
</html>`;
};

/* ── Shopping list copy ── */
const CAT_EMOJI = { 'Месо и риба': '🥩', 'Зеленчуци': '🥦', 'Зърнени и основни': '🌾', 'Млечни': '🥛', 'Плодове': '🍎', 'Плодове и ядки': '🥜', 'Мазнини и подправки': '🫒' };
const TIER_LABELS = { low: 'Нисък бюджет', standard: 'Стандарт', premium: 'Премиум' };

/* ── Shopping List ── */
const ShoppingListView = ({ list }) => {
  const [tier, setTier] = useState('standard');
  const [copied, setCopied] = useState(false);
  if (!list) return null;
  const tiers = [
    { key: 'low', label: 'Нисък', price: list.low?.weekly_estimate },
    { key: 'standard', label: 'Стандарт', price: list.standard?.weekly_estimate },
    { key: 'premium', label: 'Премиум', price: list.premium?.weekly_estimate },
  ];
  const current = list[tier];
  const handleCopy = () => {
    if (!current) return;
    let text = `🛒 СПИСЪК ЗА ПАЗАР\n📊 ${TIER_LABELS[tier]} · ${current.weekly_estimate}/седмица\n📍 portion.bg\n\n`;
    current.categories?.forEach(cat => {
      text += `${CAT_EMOJI[cat.name] || '•'} ${cat.name}\n`;
      cat.items?.forEach(item => { text += `  • ${item.name} — ${item.amount}\n`; });
      text += '\n';
    });
    navigator.clipboard.writeText(text.trim())
      .then(() => { setCopied(true); toast.success('Списъкът е копиран! Постави го в Viber или WhatsApp.'); setTimeout(() => setCopied(false), 2500); })
      .catch(() => toast.error('Неуспешно копиране'));
  };
  return (
    <div data-testid="shopping-list">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Изберете бюджетен диапазон:</p>
        <Button variant="outline" size="sm" onClick={handleCopy} data-testid="copy-shopping-list-btn"
          className={`flex items-center gap-1.5 transition-all duration-200 ${copied ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' : 'border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-400'}`}>
          {copied ? <><Check className="w-3.5 h-3.5" /> Копирано!</> : <><Copy className="w-3.5 h-3.5" /> Копирай списъка</>}
        </Button>
      </div>
      <Tabs value={tier} onValueChange={setTier}>
        <TabsList className="mb-6 w-full bg-gray-100 dark:bg-slate-700">
          {tiers.map(t => (
            <TabsTrigger key={t.key} value={t.key} className="flex-1 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-600 data-[state=active]:shadow-sm">
              <span className="text-xs font-medium">{t.label}</span>
              {t.price && <span className="hidden sm:inline ml-1.5 text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400 dark:text-gray-500">({t.price})</span>}
            </TabsTrigger>
          ))}
        </TabsList>
        {tiers.map(t => (
          <TabsContent key={t.key} value={t.key}>
            <Card className="rounded-2xl border border-gray-100 dark:border-slate-700" style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.04), 0 8px 20px rgba(0,0,0,0.05)' }}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">Седмичен списък</h4>
                  <Badge className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-700/40 font-semibold">{list[t.key]?.weekly_estimate}/седм.</Badge>
                </div>
                {current?.categories?.map((cat, ci) => (
                  <div key={ci} className="mb-5 last:mb-0">
                    <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">{cat.name}</p>
                    <div className="space-y-2">
                      {cat.items?.map((item, ii) => (
                        <div key={ii} className="flex items-center justify-between py-1.5 border-b border-gray-50 dark:border-slate-700 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{item.name}</span>
                            {item.note && <span className="text-xs text-gray-400 dark:text-gray-500 italic">{item.note}</span>}
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400 font-medium tabular-nums">{item.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

/* ── Teaser content for paywall blur ── */
const TEASER_CARDS = [
  {
    type: 'Закуска', typeBg: 'bg-amber-50', typeAccent: 'text-amber-500',
    name: 'Омлет с авокадо и домати', cal: 420, p: 28, c: 12, f: 26,
  },
  {
    type: 'Обяд', typeBg: 'bg-sky-50', typeAccent: 'text-sky-500',
    name: 'Пилешко филе с ориз и зеленчуци', cal: 580, p: 48, c: 52, f: 14,
    ingredients: ['200г пилешко филе', '80г ориз', 'смесени зеленчуци', 'зехтин'],
  },
  {
    type: 'Вечеря', typeBg: 'bg-violet-50', typeAccent: 'text-violet-500',
    name: 'Сьомга с киноа и броколи', cal: 520, p: 42, c: 38, f: 22,
  },
  {
    type: 'Снак', typeBg: 'bg-emerald-50', typeAccent: 'text-emerald-500',
    name: 'Гръцко кисело мляко с ядки', cal: 290, p: 18, c: 22, f: 14,
  },
];

const SHOP_TEASER = [
  'Пилешко филе — 600г', 'Сьомга — 400г', 'Авокадо — 3 бр.',
  'Киноа — 250г', 'Броколи — 500г', 'Гр. кисело мляко — 500г',
];

/* ── Day Navigation ── */
const DAY_TABS = ['ПОН', 'ВТОР', 'СРЯ', 'ЧЕТВ', 'ПЕТ', 'СЪБ', 'НЕД'];

const DayNavigation = ({ activeDay, onDayChange, isPreview }) => (
  <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
    {DAY_TABS.map((tab, i) => {
      const isActive = i === (isPreview ? 0 : activeDay);
      const isLocked = isPreview && i > 0;
      return (
        <button
          key={i}
          onClick={() => !isLocked && onDayChange(i)}
          aria-label={tab}
          data-testid={`day-nav-${tab.toLowerCase()}`}
          className={`relative flex-shrink-0 flex flex-col items-center justify-center gap-0.5 rounded-xl transition-all duration-200 w-[52px] h-[58px] ${
            isActive
              ? 'bg-gray-900 text-white shadow-md'
              : isLocked
              ? 'cursor-default overflow-hidden'
              : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-slate-500 hover:text-gray-800 dark:hover:text-gray-200 cursor-pointer'
          }`}
          style={isLocked ? { background: 'rgba(248,250,252,0.9)', border: '1px solid rgba(226,232,240,0.9)' } : {}}
        >
          {isLocked ? (
            <>
              <span className="text-[11px] font-semibold text-gray-300 leading-none">{tab}</span>
              <Lock className="w-3 h-3 text-gray-300 mt-0.5" />
            </>
          ) : (
            <span className={`text-[13px] font-bold leading-none ${isActive ? 'text-white' : 'text-gray-600'}`}>{tab}</span>
          )}
        </button>
      );
    })}
  </div>
);

/* ── Paywall ── */
const PaywallSection = ({ onSubscribe, isSubscribing }) => (
  <div className="mt-3" data-testid="paywall-section">
    {/* Realistic blurred teaser — Days 2–7 meal cards + shopping list */}
    <div className="relative overflow-hidden rounded-2xl">
      <div className="space-y-3 select-none pointer-events-none" style={{ filter: 'blur(5px)', opacity: 0.62 }}>
        {/* Day 2 header bar */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border border-gray-100">
          <div>
            <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest">Ден</p>
            <p className="font-semibold text-gray-900 text-sm">Вторник</p>
          </div>
          <div className="flex items-center gap-1">
            <Flame className="w-3.5 h-3.5 text-orange-400" />
            <p className="font-bold text-gray-900 text-sm">2 180 ккал</p>
          </div>
        </div>
        {/* Fake meal cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TEASER_CARDS.map((card, idx) => (
            <div key={idx} className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
              style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.04), 0 8px 20px rgba(0,0,0,0.05)' }}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-1.5">
                  <div className={`w-6 h-6 rounded-lg ${card.typeBg} flex items-center justify-center`}>
                    <Flame className={`w-3 h-3 ${card.typeAccent}`} />
                  </div>
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">{card.type}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Flame className="w-3 h-3 text-orange-400" />
                  <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{card.cal}</span>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">ккал</span>
                </div>
              </div>
              <div className="p-4">
                <p className="font-semibold text-gray-900 text-sm leading-snug mb-3">{card.name}</p>
                <div className="flex gap-1.5">
                  <div className="flex-1 bg-emerald-50 rounded-lg py-1.5 text-center">
                    <p className="text-xs font-bold text-gray-900 dark:text-gray-100">{card.p}<span className="text-[10px] font-normal text-gray-400 dark:text-gray-500">г</span></p>
                    <p className="text-[9px] text-gray-400 dark:text-gray-500">Белт.</p>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg py-1.5 text-center">
                    <p className="text-xs font-bold text-gray-900 dark:text-gray-100">{card.c}<span className="text-[10px] font-normal text-gray-400 dark:text-gray-500">г</span></p>
                    <p className="text-[9px] text-gray-400 dark:text-gray-500">Въгл.</p>
                  </div>
                  <div className="flex-1 bg-sky-50 rounded-lg py-1.5 text-center">
                    <p className="text-xs font-bold text-gray-900 dark:text-gray-100">{card.f}<span className="text-[10px] font-normal text-gray-400 dark:text-gray-500">г</span></p>
                    <p className="text-[9px] text-gray-400 dark:text-gray-500">Мазн.</p>
                  </div>
                </div>
                {card.ingredients && (
                  <div className="mt-3 space-y-1">
                    {card.ingredients.slice(0, 3).map((ing, ii) => (
                      <div key={ii} className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                        <span className="w-1 h-1 rounded-full bg-emerald-300 flex-shrink-0" />
                        {ing}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {/* Shopping list teaser */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Списък за пазар</p>
          {SHOP_TEASER.map((item, ii) => (
            <div key={ii} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 flex-shrink-0" />
                <span className="text-sm text-gray-700">{item.split(' — ')[0]}</span>
              </div>
              <span className="text-sm text-gray-400 dark:text-gray-500">{item.split(' — ')[1]}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Top fade — blends from Day 1 content into blur */}
      <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white to-transparent pointer-events-none" />
      {/* Bottom fade — dissolves blur into CTA card */}
      <div className="absolute inset-x-0 bottom-0 h-52 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none" />
    </div>

    {/* CTA card — overlaps the fade */}
    <div className="-mt-10 relative z-10">
      <Card className="border border-gray-200 rounded-2xl overflow-hidden" style={{ boxShadow: '0 4px 28px rgba(0,0,0,0.10)' }}>
        <CardContent className="p-6">
          <div className="text-center mb-5">
            <div className="w-12 h-12 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Отключи пълния план</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">Ден 2–7 + автоматичен списък за пазаруване</p>
          </div>
          <div className="space-y-2.5 mb-6">
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" />
                </div>
                <span className="text-sm text-gray-700 leading-relaxed">{b}</span>
              </div>
            ))}
          </div>
          <Button
            onClick={onSubscribe}
            disabled={isSubscribing}
            data-testid="subscribe-btn"
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-base font-bold min-h-[52px] rounded-xl flex items-center justify-center gap-2 transition-all"
            style={{ boxShadow: '0 0 24px rgba(5,150,105,0.35), 0 4px 12px rgba(5,150,105,0.20)' }}
          >
            {isSubscribing ? (
              <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Абонирай се за €7.99 / месец
              </>
            )}
          </Button>
          <p className="text-xs text-center text-gray-400 mt-2">
            Месечен абонамент · Отмени по всяко време · Без скрити такси
          </p>
        </CardContent>
      </Card>
    </div>
  </div>
);

/* ── Fridge Meal Modal ── */
const FridgeModal = ({ onClose, tdeeData }) => {
  const [tags, setTags] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [meal, setMeal] = useState(null);
  const [error, setError] = useState('');

  const addTag = (val) => {
    const trimmed = val.trim().replace(/,+$/, '');
    if (trimmed && !tags.includes(trimmed)) setTags(t => [...t, trimmed]);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(input); }
    if (e.key === 'Backspace' && !input && tags.length) setTags(t => t.slice(0, -1));
  };

  const handleSubmit = async () => {
    if (!tags.length) { setError('Добавете поне един продукт.'); return; }
    setError('');
    setLoading(true);
    setMeal(null);
    try {
      const perMeal = tdeeData?.target_calories ? Math.round(tdeeData.target_calories / 4) : null;
      const res = await axios.post(`${API}/plans/fridge-meal`, {
        ingredients: tags,
        target_calories_per_meal: perMeal,
        diet: tdeeData?.diet || 'omnivore',
      });
      setMeal(res.data.meal);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Грешка при генерирането. Опитайте отново.');
    } finally {
      setLoading(false);
    }
  };

  const cfg = MEAL_CONFIG[meal?.type] || MEAL_CONFIG.lunch;
  const MealIcon = cfg?.icon || ChefHat;

  return (
    <AnimatePresence>
      <motion.div
        key="fridge-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="fridge-modal"
      >
        <motion.div
          key="fridge-panel"
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
          transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <ChefHat className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm leading-none">Останали продукти?</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">AI ще измисли рецепта само за теб</p>
              </div>
            </div>
            <button onClick={onClose} data-testid="fridge-modal-close"
              className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center justify-center transition-colors">
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400 dark:text-gray-500" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {/* Tag input */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">
                Продукти
              </label>
              <div className="min-h-[52px] w-full rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 px-3 py-2 flex flex-wrap gap-1.5 items-center focus-within:border-emerald-400 focus-within:bg-white dark:focus-within:bg-slate-700 focus-within:ring-2 focus-within:ring-emerald-100 dark:focus-within:ring-emerald-900/30 transition-all cursor-text"
                onClick={() => document.getElementById('fridge-tag-input')?.focus()}>
                {tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400 text-xs font-medium px-2.5 py-1 rounded-full">
                    {tag}
                    <button onClick={() => setTags(t => t.filter(t2 => t2 !== tag))}
                      className="hover:text-emerald-600 transition-colors leading-none">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  id="fridge-tag-input"
                  data-testid="fridge-tag-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => { if (input.trim()) addTag(input); }}
                  placeholder={tags.length ? '' : 'напр. 3 яйца, домати, авокадо...'}
                  className="flex-1 min-w-[120px] bg-transparent text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 outline-none"
                />
              </div>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">Натисни Enter или запетая след всеки продукт</p>
            </div>

            {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}

            {/* Generated meal result */}
            {meal && !loading && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="bg-emerald-50 border border-emerald-200 rounded-2xl overflow-hidden">
                <div className="px-4 py-2.5 border-b border-emerald-200 bg-emerald-100/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-lg ${cfg.bg} flex items-center justify-center`}>
                        <MealIcon className={`w-3 h-3 ${cfg.accent}`} />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Предложена рецепта</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Flame className="w-3.5 h-3.5 text-orange-400" />
                      <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{meal.calories}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">ккал</span>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-semibold text-gray-900 mb-3 leading-snug">{meal.name}</p>
                  <div className="flex gap-2 mb-4">
                    <div className="flex-1 bg-white rounded-xl p-2 text-center border border-emerald-100">
                      <p className="text-xs font-bold text-gray-900 dark:text-gray-100">{meal.proteins}<span className="text-[10px] font-normal text-gray-400 dark:text-gray-500">г</span></p>
                      <p className="text-[9px] text-gray-400 mt-0.5">Белтъци</p>
                    </div>
                    <div className="flex-1 bg-white rounded-xl p-2 text-center border border-emerald-100">
                      <p className="text-xs font-bold text-gray-900 dark:text-gray-100">{meal.carbs}<span className="text-[10px] font-normal text-gray-400 dark:text-gray-500">г</span></p>
                      <p className="text-[9px] text-gray-400 mt-0.5">Въглехидрати</p>
                    </div>
                    <div className="flex-1 bg-white rounded-xl p-2 text-center border border-emerald-100">
                      <p className="text-xs font-bold text-gray-900 dark:text-gray-100">{meal.fats}<span className="text-[10px] font-normal text-gray-400 dark:text-gray-500">г</span></p>
                      <p className="text-[9px] text-gray-400 mt-0.5">Мазнини</p>
                    </div>
                  </div>
                  {meal.ingredients?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Съставки</p>
                      <ul className="space-y-1">
                        {meal.ingredients.map((ing, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />{ing}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {meal.recipe?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Начин на приготвяне</p>
                      <ol className="space-y-1.5">
                        {meal.recipe.map((step, i) => (
                          <li key={i} className="flex gap-2 text-xs text-gray-600 dark:text-gray-300">
                            <span className="flex-shrink-0 w-4 h-4 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center text-[9px] mt-0.5">{i + 1}</span>
                            <span className="leading-relaxed">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2">
              {meal ? (
                <>
                  <Button variant="outline" onClick={() => { setMeal(null); setTags([]); }}
                    className="flex-1 border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">
                    Нова рецепта
                  </Button>
                  <Button onClick={onClose} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm">
                    Готово
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !tags.length}
                  data-testid="fridge-generate-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white min-h-[44px] rounded-xl disabled:opacity-60"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      AI измисля рецепта…
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Генерирай рецепта
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ── Cookbook Item Card ── */
const COOKBOOK_FILTERS = [
  { key: 'all',       label: 'Всички'   },
  { key: 'breakfast', label: 'Закуска'  },
  { key: 'lunch',     label: 'Обяд'     },
  { key: 'dinner',    label: 'Вечеря'   },
  { key: 'snack',     label: 'Снак'     },
  { key: 'saved',     label: 'Запазени' },
];

const CookbookItem = ({ meal, onToggle, idx }) => {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving]     = useState(false);
  const cfg = MEAL_CONFIG[meal.type] || MEAL_CONFIG.lunch;
  const Icon = cfg.icon;

  const handleToggle = async (e) => {
    e.stopPropagation();
    setSaving(true);
    await onToggle(meal);
    setSaving(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: idx * 0.03 }}
      className={`rounded-2xl overflow-hidden border transition-all duration-200 ${cfg.border} ${cfg.cardBg}`}
    >
      {/* Header */}
      <div className="px-4 py-3 flex items-center gap-3">
        <div className={`w-8 h-8 rounded-xl ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${cfg.accent}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-bold uppercase tracking-widest ${cfg.accent}`}>{meal.type_label || cfg.label}</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate leading-tight">{meal.name}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{meal.calories} ккал</span>
          <button
            onClick={handleToggle}
            disabled={saving}
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-200 ${
              meal.is_saved
                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-400 hover:text-emerald-600'
            } ${saving ? 'opacity-50' : ''}`}
          >
            {meal.is_saved ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => setExpanded(e => !e)}
            className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100 dark:border-gray-800/60">
              {/* Macros */}
              <div className="flex gap-2">
                {[
                  { icon: Beef,    label: 'Белт.',  value: meal.proteins, color: '#34d399' },
                  { icon: Zap,     label: 'Въгл.',  value: meal.carbs,    color: '#fbbf24' },
                  { icon: Droplet, label: 'Мазн.',  value: meal.fats,     color: '#38bdf8' },
                ].map(m => (
                  <div key={m.label} className="flex-1 bg-gray-50 dark:bg-slate-800/60 rounded-xl p-2 text-center">
                    <p className="text-xs font-bold tabular-nums" style={{ color: m.color }}>{m.value}г</p>
                    <p className="text-[9px] text-gray-400">{m.label}</p>
                  </div>
                ))}
              </div>
              {/* Ingredients */}
              {meal.ingredients?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">Продукти</p>
                  <div className="flex flex-wrap gap-1">
                    {meal.ingredients.map((ing, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">{ing}</span>
                    ))}
                  </div>
                </div>
              )}
              {/* Recipe */}
              {meal.recipe && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">Рецепта</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">{meal.recipe}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ── Main Page ── */
export default function MyPlanPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [plan, setPlan] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [planCreatedAt, setPlanCreatedAt] = useState(null);
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [planHistory, setPlanHistory] = useState([]);
  const [activeDay, setActiveDay] = useState(0);
  const [activeView, setActiveView] = useState('plan');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [genStatus, setGenStatus] = useState({ remaining: 4, limit: 4, count: 0 });
  const [showFridgeModal, setShowFridgeModal] = useState(false);
  const [fridgeTags, setFridgeTags] = useState([]);
  const [fridgeInput, setFridgeInput] = useState('');
  const [fridgeLoading, setFridgeLoading] = useState(false);
  const [fridgeMeal, setFridgeMeal] = useState(null);

  // ── Left sidebar section ──
  const [activeSection, setActiveSection] = useState('plan');

  // ── Dashboard tour ──
  const [tourStep, setTourStep] = useState(null);
  const tourShownRef = useRef(false);
  const sidebarRefs = useRef([]);

  // ── Weekly Check-in ──
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [progressRefreshKey, setProgressRefreshKey] = useState(0);

  // ── Cookbook saved meals ──
  const [savedMeals, setSavedMeals] = useState(new Map()); // name -> saved_recipe_id

  // ── Cookbook section ──
  const [cookbookMeals, setCookbookMeals]     = useState([]);
  const [cookbookLoading, setCookbookLoading] = useState(false);
  const [cookbookSearch, setCookbookSearch]   = useState('');
  const [cookbookFilter, setCookbookFilter]   = useState('all');

  const tdeeData = JSON.parse(localStorage.getItem('portion_tdee') || 'null');
  const isSubscriber = user?.subscription_status === 'active';

  // ── Progress bar animation ──
  useEffect(() => {
    if (!loading) { setGenProgress(0); return; }
    setGenProgress(0);
    const STEPS = [
      { target: 20, delay: 100 }, { target: 42, delay: 200 }, { target: 60, delay: 300 },
      { target: 75, delay: 400 }, { target: 87, delay: 600 }, { target: 94, delay: 900 },
    ];
    let cur = 0, idx = 0;
    const tick = () => {
      if (idx >= STEPS.length) return;
      const { target, delay } = STEPS[idx];
      if (cur < target) { cur++; setGenProgress(cur); setTimeout(tick, delay); }
      else { idx++; setTimeout(tick, delay * 2); }
    };
    const t = setTimeout(tick, 80);
    return () => clearTimeout(t);
  }, [loading]);

  // ── Load history ──
  const loadHistory = useCallback(async () => {
    try { const res = await axios.get(`${API}/plans`); setPlanHistory(res.data); }
    catch {}
  }, []);

  // ── Load generation status ──
  const loadGenStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plans/generations-status`);
      setGenStatus(res.data);
    } catch {}
  }, []);

  // ── Load cookbook ──
  const loadCookbook = useCallback(async () => {
    setCookbookLoading(true);
    try {
      const res = await axios.get(`${API}/cookbook`);
      setCookbookMeals(res.data.meals || []);
    } catch {} finally {
      setCookbookLoading(false);
    }
  }, []);

  // ── Trigger cookbook load when section becomes active ──
  useEffect(() => {
    if (activeSection === 'cookbook') loadCookbook();
  }, [activeSection, loadCookbook]);

  // ── Toggle save from cookbook section ──
  const handleCookbookToggle = async (meal) => {
    if (meal.is_saved && meal.saved_recipe_id) {
      try {
        await axios.delete(`${API}/cookbook/save/${meal.saved_recipe_id}`);
        setCookbookMeals(prev => prev.map(m => m.name === meal.name ? { ...m, is_saved: false, saved_recipe_id: null } : m));
        setSavedMeals(prev => { const map = new Map(prev); map.delete(meal.name); return map; });
        toast.success('Премахнато от запазените');
      } catch { toast.error('Грешка при премахване'); }
    } else {
      try {
        const res = await axios.post(`${API}/cookbook/save`, {
          meal_name: meal.name,
          meal_data: { type: meal.type, type_label: meal.type_label, calories: meal.calories, proteins: meal.proteins, carbs: meal.carbs, fats: meal.fats, ingredients: meal.ingredients, recipe: meal.recipe },
          plan_id: meal.plan_id || '',
        });
        setCookbookMeals(prev => prev.map(m => m.name === meal.name ? { ...m, is_saved: true, saved_recipe_id: res.data.id } : m));
        setSavedMeals(prev => new Map(prev).set(meal.name, res.data.id));
        toast.success('Рецептата е запазена!');
      } catch (e) {
        if (e?.response?.status === 409) toast.info('Вече е запазена');
        else toast.error('Грешка при запазване');
      }
    }
  };

  // ── Handle subscription return (from Stripe) ──
  useEffect(() => {
    const subSessionId = searchParams.get('sub_session_id');
    const returnedPlanId = searchParams.get('plan_id');
    if (!subSessionId) return;
    setCheckingSubscription(true);
    toast.info('Проверяваме плащането…');
    const poll = async (attempts = 0) => {
      try {
        const res = await axios.get(`${API}/subscription/checkout/status/${subSessionId}`);
        if (res.data.payment_status === 'paid') {
          await refreshUser();
          toast.success('Абонаментът е активиран! Отключваме плана…');
          setCheckingSubscription(false);
          const pid = returnedPlanId || planId;
          if (pid) { await doUnlock(pid); }
          else { await doGenerate(); }
          return;
        }
        if (res.data.status === 'expired') {
          toast.error('Сесията е изтекла. Опитайте отново.');
          setCheckingSubscription(false);
          return;
        }
        if (attempts < 10) setTimeout(() => poll(attempts + 1), 2000);
        else { setCheckingSubscription(false); toast.error('Не можахме да потвърдим плащането. Обновете страницата.'); }
      } catch { if (attempts < 5) setTimeout(() => poll(attempts + 1), 2000); }
    };
    poll();
  }, [searchParams]); // eslint-disable-line

  // ── Auto-generate Day 1 on first visit from quiz ──
  useEffect(() => {
    loadGenStatus();
    loadHistory().then(() => {
      // Only auto-generate if tdeeData exists and no plan has been set yet
      if (tdeeData && !plan && !loading) {
        // Check if this is a fresh quiz submission (tdeeData is recent)
        const fromQuiz = sessionStorage.getItem('from_quiz');
        if (fromQuiz === 'true') {
          sessionStorage.removeItem('from_quiz');
          doGenerate();
        }
      }
    });
    // Check if weekly check-in modal should appear
    axios.get(`${API}/progress/checkin-status`)
      .then(res => { if (res.data.should_show) setShowCheckinModal(true); })
      .catch(() => {});
    // Load saved meals for bookmark state
    axios.get(`${API}/cookbook`)
      .then(res => {
        const map = new Map();
        (res.data.meals || []).forEach(m => {
          if (m.is_saved) map.set(m.name, m.saved_recipe_id);
        });
        setSavedMeals(map);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // ── Start tour after first plan load ──
  useEffect(() => {
    if (!loading && (plan || planHistory.length > 0) && !tourShownRef.current && !localStorage.getItem('portion_tour_done')) {
      tourShownRef.current = true;
      const onboardingKey = user ? `onboarding_done_${user.id || user.email}` : null;

      const startTour = () => {
        if (!localStorage.getItem('portion_tour_done')) {
          setTimeout(() => setTourStep(0), 600);
        }
      };

      if (onboardingKey && !localStorage.getItem(onboardingKey)) {
        // Wait for onboarding to be dismissed before showing tour
        const poll = setInterval(() => {
          if (localStorage.getItem(onboardingKey)) {
            clearInterval(poll);
            startTour();
          }
        }, 500);
        // Fail-safe: show tour after 35s regardless
        setTimeout(() => { clearInterval(poll); startTour(); }, 35000);
      } else {
        startTour();
      }
    }
  }, [loading, plan, planHistory, user]); // eslint-disable-line

  const doGenerate = async () => {
    if (!tdeeData) { toast.error('Попълни калкулатора първо'); navigate('/calculator'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/plans/generate`, {
        target_calories: tdeeData.target_calories,
        goal: tdeeData.goal,
        pace: tdeeData.pace,
        diet: tdeeData.diet || 'omnivore',
        gender: tdeeData.gender,
        age: tdeeData.age,
        weight: tdeeData.weight,
        height: tdeeData.height,
        activity: tdeeData.activity,
        excluded_foods: tdeeData.excludedFoods || [],
        meal_pattern: tdeeData.meal_pattern || 'standard',
      });
      setGenProgress(100);
      await new Promise(r => setTimeout(r, 400));
      setPlan(res.data.plan);
      setPlanId(res.data.plan_id);
      setPlanCreatedAt(new Date().toISOString());
      setIsPreview(res.data.is_preview);
      if (res.data.gen_remaining !== undefined) {
        setGenStatus(s => ({ ...s, remaining: res.data.gen_remaining, count: res.data.gen_limit - res.data.gen_remaining }));
      }
      localStorage.setItem('current_plan_id', res.data.plan_id);
      // Auto-save quiz weight as starting baseline for this plan's progress
      const quizWeight = tdeeData?.weight
        ? parseFloat(String(tdeeData.weight).replace(',', '.'))
        : null;
      if (quizWeight && quizWeight > 0) {
        try {
          await axios.post(`${API}/progress/checkin`, { weight: quizWeight });
          setProgressRefreshKey(k => k + 1);
        } catch {} // silent — 409 if entry for today already exists
      }
      await refreshUser();
      await loadHistory();
      if (res.data.is_preview) {
        toast.success('Ден 1 от плана ти е готов! Абонирай се за целия план.');
      } else {
        toast.success('Твоят пълен 7-дневен план е готов!');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Грешка при генерирането';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const doUnlock = async (pid) => {
    const id = pid || planId || localStorage.getItem('current_plan_id');
    if (!id) { toast.error('Не е намерен план за отключване.'); return; }
    setIsUnlocking(true);
    try {
      const res = await axios.post(`${API}/plans/${id}/unlock`);
      setPlan(res.data.plan);
      setPlanId(id);
      setIsPreview(false);
      await loadHistory();
      toast.success('Пълният план е отключен!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Грешка при отключване');
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleGenerate = () => {
    sessionStorage.setItem('from_quiz', 'false');
    doGenerate();
  };

  const handleSubscribe = async () => {
    const pid = planId || localStorage.getItem('current_plan_id') || '';
    setIsSubscribing(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/subscription/checkout`, {
        origin_url: origin,
        plan_id: pid,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Грешка при стартиране на абонамент');
      setIsSubscribing(false);
    }
  };

  const loadPlanById = async (p) => {
    try {
      const res = await axios.get(`${API}/plans/${p.id}`);
      setPlan(res.data.plan_data);
      setPlanId(p.id);
      setPlanCreatedAt(p.created_at);
      setIsPreview(p.is_preview || false);
      setActiveDay(0);
      setActiveView('plan');
      setActiveSection('plan');
      localStorage.setItem('current_plan_id', p.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { toast.error('Грешка при зареждане на плана'); }
  };

  const handleDownloadPDF = () => {
    if (!plan) return;
    toast.info('Подготвяме документа за печат…');
    const html = buildPrintHTML(plan);
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:210mm;height:297mm;border:none;visibility:hidden;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => { if (document.body.contains(iframe)) document.body.removeChild(iframe); }, 2000);
    }, 600);
  };

  const handleToggleSaveMeal = async (meal, currentPlanId, isSaved) => {
    if (isSaved) {
      const recipeId = savedMeals.get(meal.name);
      if (!recipeId) return;
      try {
        await axios.delete(`${API}/cookbook/save/${recipeId}`);
        setSavedMeals(prev => { const m = new Map(prev); m.delete(meal.name); return m; });
        toast.success('Премахнато от готварската книга');
      } catch { toast.error('Грешка при премахване'); }
    } else {
      try {
        const res = await axios.post(`${API}/cookbook/save`, {
          meal_name: meal.name,
          meal_data: {
            type: meal.type, type_label: meal.type_label,
            calories: meal.calories, proteins: meal.proteins,
            carbs: meal.carbs, fats: meal.fats,
            ingredients: meal.ingredients, recipe: meal.recipe,
          },
          plan_id: currentPlanId || planId || '',
        });
        setSavedMeals(prev => new Map(prev).set(meal.name, res.data.id));
        toast.success('Запазено в готварската книга!');
      } catch (e) {
        if (e?.response?.status === 409) toast.info('Вече е запазено');
        else toast.error('Грешка при запазване');
      }
    }
  };

  // ── Sidebar nav items ──
  const SECTIONS = [
    { id: 'plan',     label: 'Моят план',       icon: UtensilsCrossed },
    { id: 'progress', label: 'Прогрес',          icon: TrendingUp      },
    { id: 'history',  label: 'История',           icon: Clock           },
    { id: 'cookbook', label: 'Готварска книга',  icon: BookOpen        },
  ];

  // ── Render ──
  return (
    <>
      <SEOMeta
        title="Моят хранителен план"
        description="Прегледай своя 7-дневен персонализиран хранителен план с рецепти, макронутриенти и списък за пазаруване."
        canonical="/my-plan"
        noindex={true}
        breadcrumbs={PLAN_BREADCRUMBS}
      />
      <PageBreadcrumb items={PLAN_BREADCRUMBS} />

      {/* Full-screen cinematic loader during plan generation */}
      <AnimatePresence>
        {loading && <CinematicLoader progress={genProgress} />}
      </AnimatePresence>

    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>

        {/* ── Page header ── */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white mb-2">Моят хранителен план</h1>
          <p className="text-slate-500 dark:text-slate-400">Персонализиран седмичен план съобразен с твоите калории и цели</p>
        </div>

        {/* ── Mobile tab bar ── */}
        <div className="flex md:hidden gap-1 mb-6 p-1 rounded-xl bg-gray-100 dark:bg-slate-800/60">
          {SECTIONS.map(s => {
            const Icon = s.icon;
            const active = activeSection === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  active
                    ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-xs">{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* ── Layout: Sidebar + Content ── */}
        <div className="flex gap-7">

          {/* ── Left Sidebar (desktop only) ── */}
          <aside className={`hidden md:flex flex-col gap-1 w-52 flex-shrink-0 pt-1 ${tourStep !== null ? 'relative z-[56]' : ''}`}>
            {SECTIONS.map((s, sIdx) => {
              const Icon = s.icon;
              const active = activeSection === s.id;
              return (
                <button
                  key={s.id}
                  ref={el => { sidebarRefs.current[sIdx] = el; }}
                  onClick={() => setActiveSection(s.id)}
                  data-testid={`sidebar-${s.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-left transition-all duration-200 ${
                    active
                      ? 'bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700/40'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-800 dark:hover:text-gray-200'
                  } ${
                    tourStep !== null && SECTIONS[tourStep]?.id === s.id
                      ? 'ring-2 ring-emerald-400 ring-offset-1 ring-offset-white dark:ring-offset-slate-900 bg-emerald-50 dark:bg-emerald-900/30 shadow-lg shadow-emerald-500/20'
                      : ''
                  }`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-emerald-600 dark:text-emerald-400' : ''}`} />
                  {s.label}
                </button>
              );
            })}

            {/* Divider + generation counter in sidebar */}
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-800">
              <div
                data-testid="generation-counter"
                className={`rounded-xl px-3 py-3 text-xs border ${
                  genStatus.remaining === 0
                    ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400'
                    : genStatus.remaining <= 1
                    ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/40 text-amber-800 dark:text-amber-400'
                    : 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/40 text-emerald-800 dark:text-emerald-400'
                }`}
              >
                <p className="font-semibold mb-0.5">Генерирания</p>
                <p className="font-bold text-base" data-testid="generation-counter-value">
                  {genStatus.remaining} <span className="text-xs font-normal opacity-60">/ {genStatus.limit}</span>
                </p>
                {genStatus.remaining === 0 && genStatus.next_reset && (
                  <p className="mt-1 opacity-70">Нулиране: {new Date(genStatus.next_reset).toLocaleDateString('bg-BG', { day: '2-digit', month: '2-digit' })}</p>
                )}
              </div>
            </div>
          </aside>

          {/* ── Main content ── */}
          <div className="flex-1 min-w-0">

            {/* Mobile generation counter */}
            <div
              className={`flex md:hidden items-center justify-between rounded-xl px-4 py-3 mb-5 border text-sm font-medium ${
                genStatus.remaining === 0
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : genStatus.remaining <= 1
                  ? 'bg-amber-50 border-amber-200 text-amber-800'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-800'
              }`}
            >
              <span>Оставащи генерирания: <strong>{genStatus.remaining} / {genStatus.limit}</strong></span>
            </div>

            {/* ══ SECTION: МОЯ ПЛАН ══ */}
            {activeSection === 'plan' && (
              <motion.div key="plan" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>

                {/* Checking subscription */}
                {checkingSubscription && (
                  <Card className="card-shadow rounded-xl mb-8 border-emerald-200 bg-emerald-50">
                    <CardContent className="p-5 flex items-center gap-4">
                      <span className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-600 border-t-transparent flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-emerald-900">Проверяваме плащането…</p>
                        <p className="text-sm text-emerald-700">Моля изчакай, ще отключим плана автоматично.</p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Generate section (no plan yet) */}
                {!plan && !loading && (
                  <Card className="card-shadow rounded-xl mb-8">
                    <CardContent className="p-6">
                      {tdeeData ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            {[
                              { label: 'Калории', value: `${tdeeData.target_calories} ккал/ден` },
                              { label: 'Цел', value: tdeeData.goal },
                              { label: 'Темпо', value: tdeeData.pace || '—' },
                              { label: 'Абонамент', value: isSubscriber ? 'Активен ✓' : 'Безплатен' },
                            ].map(item => (
                              <div key={item.label} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3 text-center">
                                <p className="text-xs text-slate-400 mb-1">{item.label}</p>
                                <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{item.value}</p>
                              </div>
                            ))}
                          </div>
                          <Button
                            onClick={handleGenerate}
                            disabled={genStatus.remaining === 0}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white min-h-11 disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="plan-generate-button"
                          >
                            {genStatus.remaining === 0
                              ? 'Лимитът за месеца е изчерпан'
                              : isSubscriber ? 'Генерирай пълен план (7 дни)' : 'Разгледай безплатен ден 1'
                            }
                            {genStatus.remaining > 0 && <ChevronRight className="w-4 h-4 ml-1" />}
                          </Button>
                          {!isSubscriber && genStatus.remaining > 0 && <p className="text-xs text-center text-slate-400">Ден 1 е безплатен · Абонирай се за пълен достъп</p>}
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <UtensilsCrossed className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500 mb-4">Нямаш изчислени калории. Попълни калкулатора първо.</p>
                          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => navigate('/calculator')}>
                            Към калкулатора
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Generating progress — handled by CinematicLoader overlay */}

                {/* Unlocking progress */}
                {isUnlocking && (
                  <Card className="card-shadow rounded-xl mb-8 border-emerald-200 bg-emerald-50">
                    <CardContent className="p-5 flex items-center gap-4">
                      <span className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-600 border-t-transparent flex-shrink-0" />
                      <p className="font-semibold text-emerald-900">Отключваме пълния план…</p>
                    </CardContent>
                  </Card>
                )}

                {/* ── Plan display ── */}
                {plan && !loading && !isUnlocking && (
                  <div className="space-y-4">
                    {/* Plan header */}
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                            {isPreview ? 'Ден 1 — Безплатен преглед' : '7-дневен план'}
                          </h2>
                          {isPreview && (
                            <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                              Превю
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{plan.goal} • {plan.target_calories} ккал/ден</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isPreview && (
                          <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="download-pdf-btn"
                            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                            <Download className="w-4 h-4 mr-1.5" />
                            <span className="hidden sm:inline">Изтегли</span> PDF
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => { setPlan(null); setPlanId(null); setIsPreview(false); }}
                          className="border-slate-300 text-gray-700 hover:bg-gray-50 dark:bg-slate-900" data-testid="new-plan-btn">
                          <RefreshCw className="w-4 h-4 mr-1" />
                          <span className="hidden sm:inline">Нов</span> план
                        </Button>
                      </div>
                    </div>

                    {/* Day Navigation — always visible, locked for preview */}
                    <DayNavigation activeDay={activeDay} onDayChange={setActiveDay} isPreview={isPreview} />

                    {/* Subscriber unlock banner */}
                    {isPreview && isSubscriber && (
                      <Card className="border-emerald-300 bg-emerald-50 rounded-xl">
                        <CardContent className="p-4 flex items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-emerald-900">Абонаментът ти е активен!</p>
                            <p className="text-sm text-emerald-700">Отключи пълния 7-дневен план сега.</p>
                          </div>
                          <Button onClick={() => doUnlock(planId)} className="bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0">
                            Отключи сега
                          </Button>
                        </CardContent>
                      </Card>
                    )}

                    {/* View toggle — full plan only */}
                    {!isPreview && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setActiveView('plan')}
                          className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-sm font-semibold transition-all duration-150 ${
                            activeView === 'plan' ? 'bg-gray-900 dark:bg-slate-700 text-white shadow-sm' : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400'
                          }`}
                        >
                          <Calendar className="w-3.5 h-3.5" /> По дни
                        </button>
                        <button
                          onClick={() => setActiveView('shopping')}
                          className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-sm font-semibold transition-all duration-150 ${
                            activeView === 'shopping' ? 'bg-gray-900 dark:bg-slate-700 text-white shadow-sm' : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400'
                          }`}
                        >
                          <ShoppingCart className="w-3.5 h-3.5" /> Списък за пазар
                        </button>
                      </div>
                    )}

                    {/* Day content */}
                    {(activeView === 'plan' || isPreview) && plan.days?.[isPreview ? 0 : activeDay] && (
                      <motion.div key={isPreview ? 0 : activeDay} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                        {/* Day summary + Macro Rings */}
                        <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-800/60">
                          <div className="flex items-center justify-between mb-1">
                            <div>
                              <p className="text-xs text-gray-400 font-medium uppercase tracking-widest">Ден</p>
                              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{plan.days[isPreview ? 0 : activeDay].day_name}</h3>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-400 font-medium uppercase tracking-widest">Общо</p>
                              <div className="flex items-center gap-1">
                                <Flame className="w-3.5 h-3.5 text-orange-400" />
                                <span className="font-bold text-gray-900 dark:text-gray-100 tabular-nums">{plan.days[isPreview ? 0 : activeDay].total_calories}</span>
                                <span className="text-xs text-gray-400 dark:text-gray-500">ккал</span>
                              </div>
                            </div>
                          </div>
                          <MacroRings day={plan.days[isPreview ? 0 : activeDay]} plan={plan} />
                        </div>

                        {/* Staggered Meal Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {plan.days[isPreview ? 0 : activeDay].meals?.map((meal, mi) => (
                            <motion.div
                              key={mi}
                              initial={{ opacity: 0, y: 28 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.38, delay: 0.05 + mi * 0.1, ease: [0.22, 1, 0.36, 1] }}
                            >
                              <MealCard
                                meal={meal}
                                planId={planId}
                                savedMeals={isPreview ? null : savedMeals}
                                onToggleSave={isPreview ? null : handleToggleSaveMeal}
                              />
                            </motion.div>
                          ))}
                        </div>
                        {/* Paywall — immediately after meal cards, no gap */}
                        {isPreview && !isSubscriber && (
                          <PaywallSection onSubscribe={handleSubscribe} isSubscribing={isSubscribing} />
                        )}
                      </motion.div>
                    )}

                    {/* Shopping list */}
                    {activeView === 'shopping' && !isPreview && (
                      <ShoppingListView list={plan.shopping_list} />
                    )}

                    {/* Fridge FAB — only when viewing plan days (not shopping, not preview) */}
                    {!isPreview && activeView === 'plan' && (
                      <div className="flex justify-end pt-2">
                        <button
                          onClick={() => setShowFridgeModal(true)}
                          data-testid="fridge-fab"
                          className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-gray-900 text-white text-sm font-semibold shadow-lg hover:bg-gray-800 active:scale-95 transition-all duration-150"
                        >
                          <ChefHat className="w-4 h-4 text-emerald-400" />
                          Останали продукти?
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* ══ SECTION: ПРОГРЕС ══ */}
            {activeSection === 'progress' && (
              <motion.div key="progress" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Прогрес</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Следи теглото и постиженията си</p>
                </div>
                <ProgressSection
                  refreshTrigger={progressRefreshKey}
                  onAddCheckin={() => setShowCheckinModal(true)}
                  planCreatedAt={planCreatedAt}
                />
              </motion.div>
            )}

            {/* ══ SECTION: ИСТОРИЯ ══ */}
            {activeSection === 'history' && (
              <motion.div key="history" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">История на плановете</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Всички генерирани от теб планове</p>
                </div>
                {planHistory.length === 0 ? (
                  <div className="text-center py-16 text-slate-400">
                    <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p>Все още нямаш генерирани планове</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {planHistory.map(p => {
                      const tags = p.tags || {};
                      return (
                        <Card key={p.id} className="card-shadow rounded-xl" data-testid="plan-history-card">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <p className="text-xs text-slate-400">{new Date(p.created_at).toLocaleDateString('bg-BG', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                  {p.is_preview && (
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">Превю</span>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {tags.goal && <PlanTag label={tags.goal} />}
                                  {tags.calories && <PlanTag label={tags.calories} />}
                                  {tags.diet && <PlanTag label={tags.diet} />}
                                </div>
                              </div>
                              <Button variant="outline" size="sm" onClick={() => loadPlanById(p)} className="flex-shrink-0 text-xs" data-testid="open-plan-btn">
                                Отвори
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}

            {/* ══ SECTION: ГОТВАРСКА КНИГА ══ */}
            {activeSection === 'cookbook' && (() => {
              const savedCount = cookbookMeals.filter(m => m.is_saved).length;
              const filtered = cookbookMeals.filter(m => {
                if (cookbookFilter === 'saved' && !m.is_saved) return false;
                if (cookbookFilter !== 'all' && cookbookFilter !== 'saved' && m.type !== cookbookFilter) return false;
                if (cookbookSearch.trim()) {
                  const q = cookbookSearch.toLowerCase();
                  return m.name.toLowerCase().includes(q) || m.ingredients?.some(i => i.toLowerCase().includes(q));
                }
                return true;
              });
              return (
                <motion.div key="cookbook" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                  {/* Header */}
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-0.5">Готварска книга</h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {cookbookMeals.length > 0 && <span>{cookbookMeals.length} рецепти</span>}
                        {savedCount > 0 && <span className="text-emerald-600 dark:text-emerald-400"> · {savedCount} запазени</span>}
                      </p>
                    </div>
                    <button
                      onClick={loadCookbook}
                      className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-400 hover:text-emerald-600 transition-colors"
                      title="Обнови"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Search + Filter */}
                  <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        value={cookbookSearch}
                        onChange={e => setCookbookSearch(e.target.value)}
                        placeholder="Търси по ястие или съставка…"
                        data-testid="cookbook-search-sidebar"
                        className="w-full pl-8 pr-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 dark:focus:ring-emerald-900/30 transition-all"
                      />
                      {cookbookSearch && (
                        <button onClick={() => setCookbookSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Filter chips */}
                  <div className="flex gap-1.5 flex-wrap mb-4">
                    {COOKBOOK_FILTERS.map(f => (
                      <button
                        key={f.key}
                        onClick={() => setCookbookFilter(f.key)}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 ${
                          cookbookFilter === f.key
                            ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 shadow-sm'
                            : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:text-gray-700'
                        }`}
                      >
                        {f.key === 'saved' && <BookmarkCheck className="w-3 h-3" />}
                        {f.label}
                        {f.key === 'saved' && savedCount > 0 && (
                          <span className={`ml-0.5 rounded-full px-1 text-[9px] font-bold ${cookbookFilter === 'saved' ? 'bg-white/20 text-white' : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'}`}>
                            {savedCount}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Content */}
                  {cookbookLoading ? (
                    <div className="space-y-3">
                      {[1,2,3].map(i => (
                        <div key={i} className="rounded-2xl border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 h-16 animate-pulse" />
                      ))}
                    </div>
                  ) : cookbookMeals.length === 0 ? (
                    <div className="text-center py-16">
                      <BookOpen className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-slate-600" />
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Готварската книга е празна</p>
                      <button onClick={() => setActiveSection('plan')} className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:underline">
                        Генерирай план →
                      </button>
                    </div>
                  ) : filtered.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-sm text-gray-400 dark:text-gray-500">Няма резултати</p>
                      <button onClick={() => { setCookbookSearch(''); setCookbookFilter('all'); }} className="mt-2 text-xs text-emerald-600 hover:underline">
                        Изчисти филтрите
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="cookbook-sidebar-grid">
                      {filtered.map((meal, idx) => (
                        <CookbookItem
                          key={`${meal.name}-${idx}`}
                          meal={meal}
                          onToggle={handleCookbookToggle}
                          idx={idx}
                        />
                      ))}
                    </div>
                  )}

                  {!cookbookLoading && filtered.length > 0 && (
                    <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-6">
                      {filtered.length} от {cookbookMeals.length} рецепти
                    </p>
                  )}
                </motion.div>
              );
            })()}

          </div>{/* /main content */}
        </div>{/* /layout */}
      </motion.div>
    </div>

    {/* Fridge Modal */}
    {showFridgeModal && (
      <FridgeModal
        onClose={() => setShowFridgeModal(false)}
        tdeeData={tdeeData}
      />
    )}

    {/* Weekly Check-in Modal */}
    {showCheckinModal && (
      <CheckinModal
        onClose={() => setShowCheckinModal(false)}
        onSuccess={() => setProgressRefreshKey(k => k + 1)}
        initialWeight={tdeeData?.weight ?? null}
      />
    )}

    {/* Dashboard tour — first-time guided walkthrough */}
    {tourStep !== null && (
      <DashboardTour
        step={tourStep}
        sidebarRefs={sidebarRefs}
        onNext={() => setTourStep(t => t + 1)}
        onDone={() => {
          setTourStep(null);
          localStorage.setItem('portion_tour_done', 'true');
        }}
      />
    )}
    </>
  );
}
